import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import BrokenImageOutlinedIcon from '@mui/icons-material/BrokenImageOutlined';

/**
 * VideoPlayer
 *
 * Fills its parent container completely (width:100%, height:100%) so the
 * parent decides the slot size. The video is cropped/letterboxed via
 * `objectFit` (default: "cover" — fills the slot, edges may crop).
 *
 * Behavior:
 * - Autoplays when at least 50% visible in viewport, pauses when out.
 * - Click to enter native fullscreen with controls.
 * - Always renders the poster as a background so the slot is never empty
 *   while metadata is loading, or if the video fails to load.
 */
const VideoPlayer = ({
  videoUrl,
  videoPoster,
  showControls = false,
  objectFit = 'cover',
  onRatioLoaded,
}) => {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [fullScreenVideo, setFullScreenVideo] = useState(false);
  const [errored, setErrored] = useState(false);

  // Report video's natural aspect ratio to parent so it can size the slot
  const handleLoadedMetadata = (e) => {
    if (!onRatioLoaded) return;
    const v = e.currentTarget;
    if (v.videoWidth && v.videoHeight) {
      onRatioLoaded(v.videoWidth / v.videoHeight);
    }
  };

  // Autoplay/pause based on visibility
  useEffect(() => {
    const videoEl = videoRef.current;
    if (!videoEl) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !fullScreenVideo) {
            if (videoEl.paused) videoEl.play().catch(() => {});
          } else if (!videoEl.paused) {
            videoEl.pause();
          }
        });
      },
      { root: null, rootMargin: '0px', threshold: 0.5 }
    );

    observer.observe(videoEl);
    return () => observer.disconnect();
  }, [videoUrl, fullScreenVideo]);

  // Track native fullscreen state to toggle controls
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isFs = !!(document.fullscreenElement || document.webkitFullscreenElement);
      setFullScreenVideo(isFs);
      if (!isFs && videoRef.current) videoRef.current.blur();
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
    };
  }, []);

  const handleClick = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.requestFullscreen) {
      video.requestFullscreen();
    } else if (video.webkitRequestFullscreen) {
      video.webkitRequestFullscreen();
    }
  };

  if (errored) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#000',
          // Show poster as background so user still sees the thumbnail
          backgroundImage: videoPoster ? `url(${videoPoster})` : 'none',
          backgroundSize: objectFit,
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
      >
        {!videoPoster && (
          <BrokenImageOutlinedIcon sx={{ fontSize: 56, color: 'rgba(255,255,255,0.4)' }} />
        )}
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      onClick={handleClick}
      sx={{
        width: '100%',
        height: '100%',
        position: 'relative',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#000',
        overflow: 'hidden',
        cursor: 'pointer',
      }}
    >
      <video
        ref={videoRef}
        src={videoUrl}
        poster={videoPoster}
        playsInline
        loop
        muted
        controls={fullScreenVideo || showControls}
        preload="metadata"
        onLoadedMetadata={handleLoadedMetadata}
        onError={() => setErrored(true)}
        style={{
          width: '100%',
          height: '100%',
          // In native fullscreen always use `contain` so the full video is
          // visible with letterbox bars — never crop when the user has
          // explicitly opened the video.
          objectFit: fullScreenVideo ? 'contain' : objectFit,
          objectPosition: 'center',
          display: 'block',
        }}
      />
    </Box>
  );
};

export default VideoPlayer;
